import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  Camera,
  CircleDot,
  Building2,
  Lock,
  Mail,
  Trash2,
  User as UserIcon,
} from 'lucide-react'
import { Avatar } from '../../components/Avatar'
import { Button } from '../../components/ui/Button'
import { TextField, PasswordField, FieldLabel } from '../../components/ui/Field'
import {
  PhoneInputField,
  validatePhone,
} from '../../components/ui/PhoneInputField'
import {
  confirmPasswordRefine,
  makeEmailChangeSchema,
  nameSchema,
  makePasswordStrongSchema,
} from '../../lib/validators'
import { Breadcrumb } from '../../components/Breadcrumb'
import { api, extractApiError } from '../../lib/api'
import { useDetectedCountry } from '../../lib/use-detected-country'
import { useAuthStore } from '../../stores/authStore'

/** Profile picture: accepted MIME types and 2 MB cap (must match backend DTO). */
const AVATAR_MIME = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const AVATAR_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp']
const AVATAR_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp'
const AVATAR_MAX_BYTES = 2_000_000

function fileExtension(name) {
  const i = name.lastIndexOf('.')
  return i === -1 ? '' : name.slice(i + 1).toLowerCase()
}

// The local `initials(name)` helper was retired when the picture
// preview migrated to the shared <Avatar /> component (Avatar
// derives initials internally). Removed to avoid two competing
// definitions across the codebase.

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'))
    reader.readAsDataURL(file)
  })
}

/** Resize a data-URL image client-side before uploading.
 *
 *  Why: avatars are rendered at 24-80 px on the dashboard. A raw
 *  4-megapixel phone photo (~3-5 MB) is wildly oversized for that —
 *  the browser ends up doing aggressive bilinear downscaling at
 *  display time, which looks soft / blurry. Pre-sizing the source
 *  to a max of 512 px (square-fit) keeps avatars sharp on 3x
 *  retina displays (80 px × 3 = 240 px effective) while cutting
 *  the upload payload by ~90%.
 *
 *  Output format mirrors the input:
 *    - PNG / WebP source  → PNG (preserves transparency)
 *    - JPEG / GIF source  → JPEG at quality 0.92 (best compression)
 *
 *  The canvas is set up with `imageSmoothingQuality: 'high'` so the
 *  downscale uses the highest-quality resampler the browser offers.
 *  Used only for the avatar upload path — full-fidelity uploads
 *  (Files & Links) still send the original bytes. */
async function resizeImageDataUrl(dataUrl, maxSize = 512) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      try {
        const naturalW = img.naturalWidth || img.width
        const naturalH = img.naturalHeight || img.height
        if (!naturalW || !naturalH) {
          // Decode succeeded but dimensions are unknown — bail out
          // and just keep the original data URL. Better than throwing.
          resolve(dataUrl)
          return
        }
        const longestSide = Math.max(naturalW, naturalH)
        // Skip the canvas round-trip when the image is already small
        // enough — re-encoding can introduce its own quality loss.
        if (longestSide <= maxSize) {
          resolve(dataUrl)
          return
        }
        const scale = maxSize / longestSide
        const targetW = Math.round(naturalW * scale)
        const targetH = Math.round(naturalH * scale)
        const canvas = document.createElement('canvas')
        canvas.width = targetW
        canvas.height = targetH
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          resolve(dataUrl)
          return
        }
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(img, 0, 0, targetW, targetH)
        // Preserve PNG (alpha channel) for PNG / WebP sources;
        // re-encode photos as JPEG for the best size/quality ratio.
        const isPng = dataUrl.startsWith('data:image/png')
        const outputType = isPng ? 'image/png' : 'image/jpeg'
        const resized = isPng
          ? canvas.toDataURL(outputType)
          : canvas.toDataURL(outputType, 0.92)
        resolve(resized)
      } catch (err) {
        reject(err)
      }
    }
    img.onerror = () =>
      reject(new Error('Could not decode image for resize.'))
    img.src = dataUrl
  })
}

// Single Full Name field — mirrors the Sign Up page (one `fullName`
// input, the canonical shape the backend stores) so the two surfaces
// stay consistent. Same character / length rules via the shared
// `nameSchema`.
const profileSchema = z.object({
  fullName: nameSchema('Full Name'),
  contactNumber: z
    .string()
    .optional()
    .or(z.literal(''))
    .superRefine((v, ctx) => {
      const r = validatePhone(v)
      if (!r.valid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: r.message ?? 'Please enter a valid Contact Number.',
        })
      }
    }),
})

const passwordSchema = z
  .object({
    // Current Password is verified server-side by hash, so it only needs the
    // same LENGTH bounds as the policy (8–16) — matching the New Password field
    // and the QA "every input has min/max" rule — NOT the strength/character-
    // class checks (re-imposing those on an existing password is pointless and
    // would wrongly block a legacy password). Every app-set password already
    // meets 8–16, so this never locks a valid user out.
    currentPassword: z
      .string({ message: 'Current Password is required.' })
      .min(1, 'Current Password is required.')
      .min(8, 'Current Password must be at least 8 characters.')
      .max(64, 'Current Password must be at most 64 characters.'),
    // The SHARED strong-password rule with "New Password" wording (the box is
    // labelled "New Password"). Using the shared factory means this field
    // reports the SAME composite messages as the Reset-password / sign-up
    // flows — including naming an emoji / disallowed symbol explicitly, instead
    // of the old plain regex that mis-reported an emoji as a missing character
    // class.
    newPassword: makePasswordStrongSchema('New Password'),
    confirmPassword: z.string({
      message: 'Confirm New Password is required.',
    }),
  })
  .superRefine(
    confirmPasswordRefine(
      'newPassword',
      'confirmPassword',
      'Confirm New Password is required.',
    ),
  )
  .refine((d) => d.currentPassword !== d.newPassword, {
    message: 'New Password must be different from Current Password.',
    path: ['newPassword'],
  })

function CardHeader({
  icon: Icon,
  title,
}) {
  return (
    <div className="flex items-center gap-2 px-6 py-4 border-b border-[color:var(--color-rule)]">
      <Icon size={18} className="text-[color:var(--color-accent-dim)]" />
      <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
        {title}
      </h2>
    </div>
  )
}

function PersonalInformation() {
  const { user, refreshUser, applyUserPatch } = useAuthStore()
  const [submitting, setSubmitting] = useState(false)
  const fileRef = useRef(null)
  // Server IP-detected country → phone dial code when no number is set yet
  // (follows the VPN, unlike the browser-timezone guess). Null while loading /
  // unresolvable → the field keeps its own default. An EXISTING number always
  // wins: the field derives its country from the stored value.
  const detectedCountry = useDetectedCountry()
  /**
   * Pending avatar change. Stays in memory until the user clicks Save Changes:
   *   - `undefined` → no change pending
   *   - `string`    → new data URL to apply on save
   *   - `null`      → remove the existing picture on save
   */
  const [pendingAvatar, setPendingAvatar] = useState(undefined)

  const form = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      fullName: user?.fullName ?? '',
      contactNumber: '',
    },
  })

  useEffect(() => {
    // Pre-populate on user load — required by QA rule for edit forms.
    form.reset({
      fullName: user?.fullName ?? '',
      contactNumber: form.getValues('contactNumber'),
    })
    // Fetch current contact number from /me for prefill.
    api
      .get('/auth/me')
      .then((res) => {
        // contactNumber is not in the meResponse schema today — fetch directly.
        const contact = res.data?.user?.contactNumber ?? ''
        // `reset` rather than `setValue`, so the fetched number becomes the
        // form's DEFAULT. `isDirty` is measured against defaults, so with
        // `setValue` the prefilled number would look like an unsaved edit
        // and Save would be enabled on load with nothing actually changed.
        // `keepDirtyValues` protects anything the user typed while this
        // request was still in flight.
        form.reset(
          { fullName: user?.fullName ?? '', contactNumber: contact },
          { keepDirtyValues: true },
        )
      })
      .catch(() => {
        /* ignore */
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  async function onSubmit(values) {
    setSubmitting(true)
    try {
      const payload = {
        fullName: values.fullName.trim(),
        // Send the (trimmed) contact number, empty string included, so clearing
        // it reaches the backend and resets it to null. `|| undefined` dropped
        // the key, which the update service reads as "leave unchanged".
        contactNumber: values.contactNumber?.trim() ?? '',
      }
      // Only include the avatar field if the user actually picked / removed one
      // since the page loaded — keeps the payload tight.
      if (pendingAvatar !== undefined) payload.avatarUrl = pendingAvatar

      // The PATCH response includes the updated user fields
      // (id / fullName / contactNumber / avatarUrl / role). Apply
      // them directly to the auth store so the UI updates even if
      // the follow-up /auth/me refresh fails (e.g. expired access
      // cookie waiting on a server-side refresh). This is what was
      // causing the "image disappears after Save" bug: PATCH
      // succeeded, but `refreshUser()` would silently fail when the
      // session was mid-refresh and the avatar update never made
      // it into the store.
      const res = await api.patch('/users/me', payload)
      if (res?.data) {
        applyUserPatch({
          fullName: res.data.fullName,
          contactNumber: res.data.contactNumber,
          avatarUrl: res.data.avatarUrl ?? null,
        })
        // Re-sync the inputs to the values the server actually stored. The
        // backend trims + collapses stray / multiple spaces in the name, so the
        // Full Name box could otherwise keep showing the raw text the user
        // typed (e.g. "John  Doe") even though "John Doe" was saved — which read
        // as if the extra spaces had been kept. Also returns the form to a
        // pristine (not-dirty) state after a successful save.
        form.reset({
          fullName: res.data.fullName ?? '',
          contactNumber: res.data.contactNumber ?? '',
        })
      }
      // Best-effort refresh — picks up any fields the PATCH
      // response doesn't include (e.g. workRole changes from a
      // concurrent admin action). Allowed to fail silently because
      // we already updated the store optimistically above.
      void refreshUser()
      // Drop the pending state — the backend now mirrors what's on screen.
      setPendingAvatar(undefined)
      // Stable id: saving again REPLACES this toast instead of adding a
      // second one, so repeated saves can never build a stack that covers
      // the form behind it.
      toast.success('Profile updated successfully.', { id: 'profile-save' })
    } catch (err) {
      const { message, fields } = extractApiError(err)
      let mapped = false
      if (fields) {
        if (fields.fullName) {
          form.setError('fullName', { message: fields.fullName })
          mapped = true
        }
        if (fields.contactNumber) {
          form.setError('contactNumber', { message: fields.contactNumber })
          mapped = true
        }
      }
      // Shares the id with the success toast so the newest outcome
      // replaces the previous one rather than stacking beneath it.
      if (!mapped) toast.error(message, { id: 'profile-save' })
    } finally {
      setSubmitting(false)
    }
  }

  async function pickAvatar(file) {
    const ext = fileExtension(file.name)
    const mimeOk = AVATAR_MIME.includes(file.type)
    const extOk = AVATAR_EXT.includes(ext)
    if (!mimeOk || !extOk) {
      toast.error('Profile picture must be a JPG, PNG, GIF or WebP image.', {
        id: 'profile-avatar',
      })
      return
    }
    if (file.size > AVATAR_MAX_BYTES) {
      toast.error('Image must be 2 MB or smaller.', { id: 'profile-avatar' })
      return
    }
    try {
      const rawDataUrl = await readFileAsDataUrl(file)
      // Downscale to a 512 px max longest-side before staging. This
      // is what fixes the "avatar looks blurry" complaint: rendering
      // a 4 MP photo at a 28 px circle forces the browser into
      // aggressive runtime downscaling, which softens edges. Pre-
      // sizing to 512 px keeps avatars sharp on 3x retina (the
      // 80 px Profile preview maps to 240 effective pixels at 3x,
      // well under 512) while trimming the upload payload.
      const dataUrl = await resizeImageDataUrl(rawDataUrl, 512)
      // Stage the change locally — actual upload happens on Save Changes.
      setPendingAvatar(dataUrl)
      // Confirm the pick straight away, but say plainly that it is NOT
      // saved yet. A bare "uploaded successfully" here would be false —
      // nothing has been sent to the server, and navigating away without
      // saving silently discards the picture.
      toast.success('Picture selected — click "Save Changes" to apply it.', {
        id: 'profile-avatar',
      })
    } catch {
      toast.error('Could not read the selected image.', { id: 'profile-avatar' })
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  function markAvatarForRemoval() {
    setPendingAvatar(null)
    // Same reasoning as `pickAvatar`: the removal is staged, not applied.
    // This case is the more confusing of the two — the avatar vanishes
    // from the preview and the Remove button disappears with it, so
    // without this the user reasonably assumes it is already gone.
    toast.success(
      'Picture marked for removal — click "Save Changes" to apply it.',
      { id: 'profile-avatar' },
    )
  }

  /**
   * Gate for the Save button. The picture is staged OUTSIDE react-hook-form
   * (in `pendingAvatar`), so `isDirty` on its own would miss a picked or
   * removed photo and leave Save disabled with a real change waiting.
   *
   * Both signals clear themselves on a successful save — `onSubmit` resets
   * the form to the server's values and sets `pendingAvatar` back to
   * `undefined` — so the button returns to disabled once the save lands.
   */
  const hasUnsavedChanges =
    form.formState.isDirty || pendingAvatar !== undefined

  // What the avatar circle should currently show
  const displayedAvatar =
    pendingAvatar === undefined ? user?.avatarUrl ?? null : pendingAvatar

  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm">
      <CardHeader icon={UserIcon} title="Personal Information" />
      <div className="px-6 pt-6">
        <FieldLabel label="Profile Picture" />
        <div className="flex items-center gap-4">
          {/* Shared <Avatar /> — renders the current avatar (or
           *  pending upload) and gracefully falls back to initials
           *  on the brand gradient if the URL is broken. Sized `xl`
           *  (80px) to match the previous inline tile. */}
          <Avatar
            name={user?.fullName ?? ''}
            avatarUrl={displayedAvatar}
            size="2xl"
            placeholder="icon"
          />
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept={AVATAR_ACCEPT}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void pickAvatar(file)
                }}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={() => fileRef.current?.click()}
              >
                <Camera size={14} />
                {displayedAvatar ? 'Change Picture' : 'Upload Picture'}
              </Button>
              {displayedAvatar ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={markAvatarForRemoval}
                >
                  <Trash2 size={14} />
                  Remove
                </Button>
              ) : null}
            </div>
            <p className="text-xs text-[color:var(--color-ink-muted)]">
              JPG, PNG, GIF or WebP. Up to 2 MB.
            </p>
            {/* Dimension guidance. The avatar renders inside a circle with
             *  `object-cover`, so a non-square picture is CENTRE-CROPPED,
             *  never stretched — say exactly that, so the wording matches
             *  what actually happens. 256×256 is the floor worth
             *  suggesting: uploads are downscaled to a 512px longest side
             *  (`resizeImageDataUrl`) and the largest on-screen avatar is
             *  80px, which is 240px on a 3x display. */}
            <p className="text-xs text-[color:var(--color-ink-muted)]">
              Square images work best — 256×256 or larger. Other shapes are
              centred and cropped to fill the circle, not stretched.
            </p>
            {/* Persistent pending-state hint. The toast fired on pick /
             *  remove disappears after a few seconds, but the change stays
             *  unsaved until the form is submitted — so keep an always-on
             *  reminder next to the preview. Mirrors the "Unsaved changes"
             *  treatment on the Report Schedule page. */}
            {pendingAvatar !== undefined ? (
              <p className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--color-warning)]">
                <CircleDot size={12} />
                {pendingAvatar === null
                  ? 'Picture will be removed when you save.'
                  : 'New picture not saved yet.'}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="p-6 space-y-4">
        {/* `maxLength` is 50, not 100, because 50 is what's actually
         *  enforced: `profileSchema` uses `nameSchema('Full Name')`, whose
         *  default cap is 50 — the same cap every other Full Name field in
         *  the app uses (see ExternalCollaboratorsPage / MembersPage).
         *  With 100 here the browser let you type 51-100 characters that
         *  the form would then reject on submit, and silently swallowed
         *  anything past 100 with no feedback at all. `showCount` surfaces
         *  the live "n/50" counter, which turns red at the cap. */}
        <TextField
          label="Full Name"
          required
          placeholder="Enter the Full Name"
          minLength={2}
          maxLength={50}
          showCount
          error={form.formState.errors.fullName?.message}
          {...form.register('fullName')}
        />

        <div>
          <FieldLabel label="Email ID" htmlFor="profile-email" />
          <div className="relative">
            <Mail
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-subtle)]"
              aria-hidden="true"
            />
            {/* Stays in the tab order on purpose. `readonly` (unlike
             *  `disabled`) is meant to remain focusable so keyboard and
             *  screen-reader users can still REACH and read the value —
             *  removing it with tabindex="-1" would hide the user's own
             *  email from exactly the people this is meant to help.
             *  Screen readers already announce native `readonly`, so no
             *  redundant aria-readonly; what was missing is the WHY,
             *  which `aria-describedby` now supplies. */}
            <input
              id="profile-email"
              type="email"
              readOnly
              aria-describedby="profile-email-hint"
              value={user?.emailId ?? ''}
              className="w-full h-10 pl-9 pr-9 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] cursor-not-allowed"
            />
            {/* Lock affordance. `aria-hidden` on purpose — the locked state
             *  already reaches assistive tech twice over (native `readonly`
             *  is announced, and `aria-describedby` gives the reason), so
             *  exposing the icon too would just be noise. This is the
             *  visual half of the same message, for sighted users who
             *  otherwise only see a grey box. */}
            <Lock
              size={13}
              aria-hidden="true"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-subtle)] pointer-events-none"
            />
          </div>
          <p
            id="profile-email-hint"
            className="mt-1.5 text-xs text-[color:var(--color-ink-muted)]"
          >
            Your sign-in address — this can&apos;t be changed here.
          </p>
        </div>

        {/* Department — read-only; a member's department is managed by the
         *  Company Admin, not editable here. Hidden for tenant admins who
         *  have no department. */}
        {user?.departmentName ? (
          <div>
            <FieldLabel label="Department" htmlFor="profile-department" />
            <div className="relative">
              <Building2
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-subtle)]"
                aria-hidden="true"
              />
              {/* Same reasoning as the Email field above: focusable by
               *  design, with the reason exposed via aria-describedby. */}
              <input
                id="profile-department"
                type="text"
                readOnly
                aria-describedby="profile-department-hint"
                value={user.departmentName}
                className="w-full h-10 pl-9 pr-9 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] cursor-not-allowed"
              />
              {/* Same lock affordance as Email — see the note there. */}
              <Lock
                size={13}
                aria-hidden="true"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-subtle)] pointer-events-none"
              />
            </div>
            <p
              id="profile-department-hint"
              className="mt-1.5 text-xs text-[color:var(--color-ink-muted)]"
            >
              Set by your Company Admin — this can&apos;t be changed here.
            </p>
          </div>
        ) : null}

        <PhoneInputField
          // Remount once the IP country arrives so the new default dial code
          // applies to an empty field; an existing number's country is
          // preserved (the field reads it from `value`).
          key={detectedCountry ?? 'auto'}
          defaultCountry={detectedCountry ?? undefined}
          label="Contact Number"
          id="profile-contact"
          value={form.watch('contactNumber') ?? ''}
          onChange={(v) =>
            // `shouldDirty` is REQUIRED — RHF's setValue doesn't mark the field
            // dirty on its own, so without it a contact-number-only change never
            // flips `formState.isDirty` and Save Changes stays disabled (the
            // registered Full Name field gets dirty tracking for free).
            form.setValue('contactNumber', v, {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          error={form.formState.errors.contactNumber?.message}
        />

        <div className="pt-2 flex justify-start">
          <Button
            type="submit"
            loading={submitting}
            disabled={!hasUnsavedChanges}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </section>
  )
}

/**
 * Strength grading for the New Password field.
 *
 * Hand-rolled rather than pulling in zxcvbn: this bundle ships without
 * code splitting, and shipping a ~800KB dictionary to grade a field
 * that's capped at 16 characters isn't a trade worth making. It also
 * matches the house style — the UI primitives and charts here are all
 * hand-rolled.
 *
 * The grades deliberately track the rule the form ACTUALLY enforces
 * (8-16 chars + lower + upper + digit + special). Anything that would be
 * rejected is graded "Weak" — the meter must never show a reassuring
 * colour for a password the form is about to refuse. Since all four
 * classes are mandatory, length is the only axis left to improve, so
 * that's what separates Medium from Strong.
 *
 * Returns `null` for an empty field so the meter stays hidden until the
 * user actually starts typing.
 */
function passwordStrength(value) {
  const v = value ?? ''
  if (!v) return null
  const classes =
    (/[a-z]/.test(v) ? 1 : 0) +
    (/[A-Z]/.test(v) ? 1 : 0) +
    (/\d/.test(v) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(v) ? 1 : 0)
  if (v.length < 8 || classes < 4) {
    return { label: 'Weak', pct: 33, token: 'danger' }
  }
  if (v.length < 12) return { label: 'Medium', pct: 66, token: 'warning' }
  return { label: 'Strong', pct: 100, token: 'success' }
}

/** Bar + label for `passwordStrength`. Colours come through inline
 *  `style` on purpose: the token varies at runtime, and Tailwind only
 *  generates classes it can see in the source, so a interpolated class
 *  name would silently produce no CSS. `aria-live` announces the grade
 *  to screen-reader users as it changes. */
function PasswordStrengthMeter({ value }) {
  const strength = passwordStrength(value)
  if (!strength) return null
  const colour = `var(--color-${strength.token})`
  return (
    <div className="mt-1.5 flex items-center gap-2" aria-live="polite">
      <div className="h-1 flex-1 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${strength.pct}%`, backgroundColor: colour }}
        />
      </div>
      <span
        className="text-[11px] font-semibold shrink-0"
        style={{ color: colour }}
      >
        {strength.label}
      </span>
      <span className="sr-only">password strength</span>
    </div>
  )
}

function ChangePassword() {
  const { signOut } = useAuthStore()
  const [submitting, setSubmitting] = useState(false)

  const form = useForm({
    resolver: zodResolver(passwordSchema),
    // Default RHF mode is `onSubmit`, which is why nothing was flagged
    // until the button was pressed. `onTouched` surfaces each field's
    // error once the user leaves it (and live-updates while they fix
    // it), without turning the box red on the very first keystroke.
    mode: 'onTouched',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const newPassword = form.watch('newPassword')
  const confirmPassword = form.watch('confirmPassword')

  /**
   * Instant "Passwords do not match" feedback, while the user is still
   * typing in Confirm.
   *
   * `mode: 'onTouched'` only starts validating a field once it has been
   * blurred, so the first time someone types into Confirm — before they
   * tab away — the resolver stays silent and they'd see nothing until
   * they left the field or pressed the button. Comparing the two watched
   * values directly surfaces the message the moment they differ, which
   * is the instant feedback QA asked for, while still not turning the
   * New Password box red on keystroke one.
   *
   * The resolver's own error takes precedence whenever it has one (it
   * also covers the empty-Confirm "required" case), and the wording here
   * is copied from `confirmPasswordRefine` so the two can't drift apart.
   */
  const liveMismatch =
    newPassword && confirmPassword && newPassword !== confirmPassword
      ? 'Passwords do not match.'
      : undefined

  async function onSubmit(values) {
    setSubmitting(true)
    try {
      await api.post('/users/me/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
      form.reset()
      // The backend revokes EVERY session on a password change, so the current
      // token is already dead. Sign out immediately — clearing client auth
      // flips the store to `unauthenticated`, which the route guard follows by
      // redirecting to /sign-in — instead of stranding the user on a live-
      // looking page until their next request 401s. The success toast persists
      // across the redirect and greets them on the sign-in screen.
      toast.success('Password changed. Please sign in again with your new password.', {
        id: 'password-change',
      })
      await signOut()
      return
    } catch (err) {
      const { message, fields } = extractApiError(err)
      let mapped = false
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          if (name in form.getValues()) {
            form.setError(name, { message: msg })
            mapped = true
          }
        }
      }
      // Only toast when we couldn't attach the error inline. Shares the
      // id with the success toast so the newest outcome replaces the old.
      if (!mapped) toast.error(message, { id: 'password-change' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm">
      <CardHeader icon={Lock} title="Change Password" />
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="p-6 space-y-4">
        <PasswordField
          label="Current Password"
          required
          autoComplete="current-password"
          placeholder="Enter the Current Password"
          maxLength={128}
          error={form.formState.errors.currentPassword?.message}
          {...form.register('currentPassword')}
        />
        <div>
          <PasswordField
            label="New Password"
            required
            autoComplete="new-password"
            placeholder="Enter a New Password"
            maxLength={128}
            error={form.formState.errors.newPassword?.message}
            {...form.register('newPassword')}
          />
          <PasswordStrengthMeter value={newPassword} />
        </div>
        <PasswordField
          label="Confirm New Password"
          required
          autoComplete="new-password"
          placeholder="Re-enter New Password"
          maxLength={128}
          error={form.formState.errors.confirmPassword?.message ?? liveMismatch}
          {...form.register('confirmPassword')}
        />

        <div className="pt-2 flex justify-start">
          <Button type="submit" loading={submitting}>
            Change Password
          </Button>
        </div>
      </form>
    </section>
  )
}

/**
 * Change the sign-in Email ID.
 *
 * A sibling of ChangePassword, not a field on Personal Information: the
 * address IS a credential, so it carries the same current-password proof and
 * the same sign-out-on-success. Deliberately does NOT use `applyUserPatch` —
 * the backend revokes every session, so there is no "stay on the page with the
 * new value" state to render.
 */
function ChangeEmail() {
  const { signOut } = useAuthStore()
  const currentEmail = useAuthStore((s) => s.user?.emailId)
  // Server-reported credential state. A Google-only account has no password to
  // prove, so the field is shown as optional rather than blocking the form.
  const hasPassword = useAuthStore((s) => s.user?.hasPassword !== false)
  const [submitting, setSubmitting] = useState(false)

  const form = useForm({
    resolver: zodResolver(makeEmailChangeSchema(hasPassword)),
    mode: 'onTouched',
    defaultValues: { newEmailId: '', currentPassword: '' },
  })

  async function onSubmit(values) {
    setSubmitting(true)
    try {
      await api.post('/users/me/change-email', {
        newEmailId: values.newEmailId,
        currentPassword: values.currentPassword,
      })
      // Every session is dead server-side (the login identifier moved), so sign
      // out rather than strand the user on a live-looking page until their next
      // request 401s. Same handling as a password change.
      toast.success('Email ID updated. Please sign in again with your new Email ID.', {
        id: 'email-change',
      })
      await signOut()
      return
    } catch (err) {
      const { message, fields } = extractApiError(err)
      let mapped = false
      if (fields) {
        for (const [name, msg] of Object.entries(fields)) {
          // The backend names the field `emailId`; the form field is
          // `newEmailId` (there are two addresses on screen).
          const target = name === 'emailId' ? 'newEmailId' : name
          if (target in form.getValues()) {
            form.setError(target, { message: msg })
            mapped = true
          }
        }
      }
      if (!mapped) toast.error(message, { id: 'email-change' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm">
      <CardHeader icon={Mail} title="Change Email ID" />
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="p-6 space-y-4">
        <div>
          <FieldLabel label="Current Email ID" htmlFor="current-email" />
          <input
            id="current-email"
            type="email"
            readOnly
            value={currentEmail ?? ''}
            className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] cursor-not-allowed"
          />
        </div>
        <TextField
          label="New Email ID"
          required
          type="email"
          autoComplete="email"
          placeholder="Enter a New Email ID"
          maxLength={100}
          error={form.formState.errors.newEmailId?.message}
          {...form.register('newEmailId')}
        />
        {/* Hidden entirely for a Google-only account: there is no password on
         *  the account, so an empty "optional" box would just be a dead field
         *  the user can't fill. Password accounts see it required, as before. */}
        {hasPassword ? (
          <PasswordField
            label="Current Password"
            required
            autoComplete="current-password"
            placeholder="Enter the Current Password"
            maxLength={128}
            error={form.formState.errors.currentPassword?.message}
            {...form.register('currentPassword')}
          />
        ) : null}
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          This is the address you sign in with. Changing it signs you out
          everywhere, and the old address will no longer work.
          {hasPassword
            ? ''
            : ' Your account signs in with Google, so no password is needed here.'}
        </p>

        <div className="pt-2 flex justify-start">
          <Button type="submit" loading={submitting}>
            Change Email ID
          </Button>
        </div>
      </form>
    </section>
  )
}

export function ProfilePage({ homePath, homeLabel, padded } = {}) {
  const body = (
    <>
      <Breadcrumb current="My Profile" homePath={homePath} homeLabel={homeLabel} />

      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-[color:var(--color-ink-strong)]">
          My Profile
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Manage your account and password.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PersonalInformation />
        <ChangeEmail />
        <ChangePassword />
        {/* Display, accessibility, region & formats (and, for people who can
         *  manage the company, the company country + timezone) live on My
         *  Preferences, reached from the avatar menu — no signpost card here.
         *  The "Hide me from the leaderboard" opt-out was removed on
         *  2026-10-09: everyone in scope appears on the leaderboard. */}
      </div>
    </>
  )

  return padded ? <div className="p-8">{body}</div> : <div>{body}</div>
}
